import { useEffect, useLayoutEffect, useMemo, useState, useCallback } from 'react';
import { Outlet, useLocation, useNavigate, useRouteLoaderData } from 'react-router-dom';
import ScopeGuard from 'ui-component/ScopeGuard';

// material-ui
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import AppBar from '@mui/material/AppBar';
import Container from '@mui/material/Container';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import { LicenseInfo } from '@mui/x-license';
LicenseInfo.setLicenseKey('b5003060823ab69c43cc8c1f6ded20bcTz0xMDYwMzgsRT0xNzY4NjA3OTk5MDAwLFM9cHJvLExNPXBlcnBldHVhbCxQVj1RMy0yMDI0LEtWPTI=');

// project imports
import Footer from './Footer';
import Header from './Header';
import Sidebar from './Sidebar';
import HorizontalBar from './HorizontalBar';
import MainContentStyled from './MainContentStyled';
import Loader from 'ui-component/Loader';
import Breadcrumbs from 'ui-component/extended/Breadcrumbs';
import TechnicalSupportBanner from './TechnicalSupportBanner';
import SubscriptionBanners from './SubscriptionBanners';
import { HeaderActionsProvider } from 'contexts/HeaderActionsContext';

import { MenuOrientation, ThemeMode } from 'config';
import useConfig from 'hooks/useConfig';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';
import Customization from '../Customization';
import axios from 'axios';
import Cookies from 'universal-cookie';
import useDashboardTour, { shouldSkipFreeTrialModal } from 'hooks/useDashboardTour';
import FreeTrialDialog from 'components/FreeTrialDialog';
import { CloudRestApiAccessProvider } from 'contexts/CloudRestApiAccessContext';
import {
  syncManagerRoleFromSession,
  isRevokedManagerSession,
  persistEnrichedLiveContexts,
  extractLiveAccountContexts,
  CONTEXT_TYPE_MANAGER,
  loadPersistedAccountContexts,
  syncActiveManagerAccessFromLive,
} from 'utils/accountContext/accountContextUtils';
import useApi from 'hooks/useApi';
import dialogEventEmitter from 'utils/dialogEventEmitter';
const cookies = new Cookies();
const SUPPORT_ACCESS_LEVELS = ['s1', 's2', 's3'];

// ==============================|| MAIN LAYOUT ||============================== //

export default function MainLayout() {
  const theme = useTheme();
  const downMD = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { borderRadius, container, miniDrawer, menuOrientation, hydrateLayoutFromServer } = useConfig();
  const { patch } = useRouteLoaderData('main');
  /** Apply `main` route loader data before paint (loader already awaited customize GET). */
  useLayoutEffect(() => {
    void hydrateLayoutFromServer(patch);
  }, [patch, hydrateLayoutFromServer]);
  const { menuMaster, menuMasterLoading } = useGetMenuMaster();
  const drawerOpen = menuMaster?.isDashboardDrawerOpened ?? false;
  
  // supportAccessSt: "1" = show technical support banner, "0" = hide (string, matches cookie).
  // The polling sync in NotificationSection only writes that cookie for users whose
  // role allows TechSupport, so support sessions naturally never see the banner.
  const [supportAccessGranted, setSupportAccessGranted] = useState(() => {
    const supportAccessSt = cookies.get('supportAccessSt');
    if (supportAccessSt === true || supportAccessSt === 1 || supportAccessSt === '1') {
      return '1';
    }
    return '0';
  });

  // Header actions (e.g. Reboot/More on device pages) - set by child routes, shown in breadcrumb row
  const [headerActions, setHeaderActions] = useState(null);

  // Hide sidebar on newLogin page
  const isNewLoginPage = location.pathname === '/newLogin' || location.pathname.endsWith('/newLogin');
  const isSupportRequestPage = location.pathname === '/support/usersList' || location.pathname.endsWith('/support/usersList');
  // Hide breadcrumbs on user-profile pages
  const isUserProfilePage = location.pathname === '/user-profile' || location.pathname.startsWith('/user-profile/');

  useEffect(() => {
    const accessLevel = localStorage.getItem('accessLevel');
    const isSupportUser = SUPPORT_ACCESS_LEVELS.includes(accessLevel) || !!cookies.get('userSubRole');
    if (!isSupportUser) return;

    const path = location.pathname || '';
    const skipPaths = ['/', '/failSafeSupport', '/landingPage', '/muiSupport', '/premiumUser', '/support/usersList'];
    if (skipPaths.some((skip) => path === skip)) return;

    dialogEventEmitter.emit('supportSessionBootstrap');
  }, [location.pathname]);
  
  // Initialize tour guide - available on all pages
  const { TourComponent, showFreeTrialModal, setShowFreeTrialModal, startTour } = useDashboardTour(false);

  // Handle Add Device button click - keep MSP scope and auto-open add-device drawer
  const handleAddDevice = useCallback(() => {
    setShowFreeTrialModal(false);
    // Use MSP route to avoid scope restoration to last location.
    navigate('/mspHome/devices?mode=add');
  }, [navigate, setShowFreeTrialModal]);

  // Handle Will Add Later button click - close modal
  const handleAddLater = useCallback(() => {
    setShowFreeTrialModal(false);
  }, [setShowFreeTrialModal]);

  // Handle dialog close
  const handleCloseDialog = useCallback(() => {
    setShowFreeTrialModal(false);
  }, [setShowFreeTrialModal]);

  const hasDrawer = !isNewLoginPage && !isSupportRequestPage;

  useEffect(() => {
    syncManagerRoleFromSession(cookies);
  }, [location.pathname, location.state?.accountSwitched]);

  useEffect(() => {
    let cancelled = false;
    let inFlight = false;

    const redirectIfManagerAccessRevoked = async () => {
      if (inFlight || cancelled) {
        return;
      }
      if (cookies.get('userSubRole')) {
        return;
      }
      if (localStorage.getItem('insightActiveContextType') !== CONTEXT_TYPE_MANAGER) {
        return;
      }
      if (loadPersistedAccountContexts().length === 0) {
        return;
      }
      inFlight = true;
      try {
        const result = await useApi('GET_ACCOUNT_CONTEXTS');
        if (cancelled) return;
        const { ok, contexts } = extractLiveAccountContexts(result);
        if (!ok) return;
        const live = persistEnrichedLiveContexts(contexts);
        if (isRevokedManagerSession(live)) {
          navigate('/account-selection', {
            replace: true,
            state: { contexts: live, switchMode: true, accessRevoked: true },
          });
          return;
        }
        // AccessType change while still on the same account (e.g. R/W → R/O).
        syncActiveManagerAccessFromLive(live, cookies);
      } catch {
        // Keep the current session if the live check fails.
      } finally {
        inFlight = false;
      }
    };

    redirectIfManagerAccessRevoked();
    const intervalId = window.setInterval(redirectIfManagerAccessRevoked, 10000);
    const onFocus = () => {
      redirectIfManagerAccessRevoked();
    };
    window.addEventListener('focus', onFocus);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        redirectIfManagerAccessRevoked();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [location.pathname, navigate]);

  // Listen for support access granted/revoked events. Revocation can come
  // from the banner's End Access button or from the periodic poll in
  // NotificationSection (mirrors classic `setTechSupport` Redux dispatch).
  useEffect(() => {
    const handleSupportAccessGranted = () => {
      setSupportAccessGranted('1');
    };
    const handleSupportAccessRevoked = () => {
      setSupportAccessGranted('0');
    };
    const handleStartDashboardTour = () => {
      startTour();
    };

    window.addEventListener('supportAccessGranted', handleSupportAccessGranted);
    window.addEventListener('supportAccessRevoked', handleSupportAccessRevoked);
    window.addEventListener('startDashboardTour', handleStartDashboardTour);
    return () => {
      window.removeEventListener('supportAccessGranted', handleSupportAccessGranted);
      window.removeEventListener('supportAccessRevoked', handleSupportAccessRevoked);
      window.removeEventListener('startDashboardTour', handleStartDashboardTour);
    };
  }, [startTour]);

  // Handle end support access
  const handleEndSupportAccess = useCallback(() => {
    setSupportAccessGranted('0');
    // Cookie is already updated in TechnicalSupportBanner component
  }, []);

  // MUI access check function - reusable for immediate check and polling
  const checkMuiAccess = useCallback(async () => {
    try {
      
      const muiWebSupport = (await axios.get(`${process.env.apiHost}public/v1/muiStatus/${cookies.get('_Id')}`, {
        headers: {
          'Content-Type': 'application/json; charset=UTF-8',
          "apikey": process.env.apiKey,
          'Accept': 'application/json'
        },
        withCredentials: true,
        timeout: 60000
      })).data;
      if (muiWebSupport?.response?.status) {
        if (muiWebSupport?.details?.muiWebSupport == "0") {
          navigate('/muiSupport');
          return false; // Access denied
        }
      }
      return true; // Access granted
    } catch (error) {
      console.error('Error checking MUI access:', error);
      return true; // On error, allow access (fail open to avoid blocking legitimate users)
    }
  }, [navigate]);

  // Immediate MUI access check on mount and route changes - catches direct URL access
  useEffect(() => {
    const userId = cookies.get('_Id');
    if (userId) {
      // Run check immediately when component mounts or route changes
      checkMuiAccess();
    }
  }, [location.pathname, checkMuiAccess]);

  // Polling interval for MUI access check (runs every 30 seconds)
  useEffect(() => {
    const userId = cookies.get('_Id');
    if (!userId) {
      // Don't start polling if no user ID is present
      return;
    }

    let muiIntervalId = setInterval(async () => {
      const hasAccess = await checkMuiAccess();
      if (!hasAccess) {
        clearInterval(muiIntervalId);
        return;
      }
    }, 30000);
    
    return () => {
      clearInterval(muiIntervalId);
    };
  }, [checkMuiAccess]);

  // Handle drawer open state
  useEffect(() => {
    handlerDrawerOpen(!miniDrawer);
  }, [miniDrawer]);

  useEffect(() => {
    if (downMD) {
      // Close drawer on mobile view
      handlerDrawerOpen(false);
    } else {
      // Reopen drawer when returning to desktop view (respects miniDrawer setting)
      handlerDrawerOpen(!miniDrawer);
    }
  }, [downMD, miniDrawer]);

  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downMD;

  // horizontal menu-list bar : drawer
  const menu = useMemo(() => (isHorizontal ? <HorizontalBar /> : <Sidebar />), [isHorizontal]);

  // Skip loading check for support page since it doesn't need menu
  // For support page, we don't need to wait for menu to load
  if (menuMasterLoading && !isSupportRequestPage) return <Loader />;

  return (
    <CloudRestApiAccessProvider>
    <Box sx={{ display: 'flex' }}>
      {/* header */}
      <AppBar enableColorOnDark position="fixed" color="inherit" elevation={0} sx={{ bgcolor: theme.palette.mode === ThemeMode.DARK ? theme.palette.dark[900] : theme.palette.primary.main, borderBottom: theme.palette.mode === ThemeMode.DARK ? 'solid 1px' : 'transparent 1px', borderColor: theme.palette.divider,}}>
        <Toolbar sx={{ p: isHorizontal ? 1.25 : 0 }}>
          <Header startTour={startTour}/>
        </Toolbar>
      </AppBar>

      {/* menu / drawer */}
      {!isNewLoginPage && !isSupportRequestPage && menu}

      {/* main content */}
      <MainContentStyled
        borderRadius={borderRadius}
        menuOrientation={menuOrientation}
        open={hasDrawer && drawerOpen}
        hasDrawer={hasDrawer}
        isNewLoginPage={isNewLoginPage}
      >
       <Container
          maxWidth={container ? 'xxl' : false}
          sx={{ 
            ...(!container && { px: { xs: 3 } }), 
            minHeight: 'calc(100vh - 128px)', 
            display: 'flex', 
            flexDirection: 'column',
            pb: supportAccessGranted === '1' ? 8 : 0
          }}
        >
          {/* breadcrumb */}
          <HeaderActionsProvider value={setHeaderActions}>
            {!isNewLoginPage && !isUserProfilePage && <Breadcrumbs headerActions={headerActions} />}
            {/* Joyride Tour - available on all pages */}
            <TourComponent />
            {/* Subscription banners (e.g., active free-trial info) */}
            {!isNewLoginPage && <SubscriptionBanners />}
            <ScopeGuard>
              <Outlet />
            </ScopeGuard>
          </HeaderActionsProvider>
          {/* <Footer /> */}
        </Container>
      </MainContentStyled>
      {/* Technical Support Banner */}
      <TechnicalSupportBanner 
        isVisible={supportAccessGranted} 
        onEndAccess={handleEndSupportAccess}
      />
      {/* Free Trial Dialog - shown when tour finishes */}
      <FreeTrialDialog
        open={showFreeTrialModal && !shouldSkipFreeTrialModal()}
        onAddDevice={handleAddDevice}
        onAddLater={handleAddLater}
        onClose={handleCloseDialog}
      />
      {/* <Customization /> Moved to header */}
    </Box>
    </CloudRestApiAccessProvider>
  );
}
